import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Alert, Badge } from 'react-bootstrap'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import type { TaskStatusActionConfig, TaskActionHookType } from '@ztechapps/common.contract'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import { useError } from '@ztechapps/common.hook'
import { useTaskActionHooks, useUpdateTaskStatusActions } from '@ztechapps/task.common'
import { Icon, Spinner } from '@ztechapps/webui.util'
import { BusyButton } from '@ztechapps/webui.button'
import { Select } from '@ztechapps/webui.input'

// ---------------------------------------------------------------------------
// Shared types & helpers
// ---------------------------------------------------------------------------

export type StatusActionInput = {
  status: TaskStatusEnum
  actionHookId: string
}

export type StatusActionsBadgeProps = {
  count?: number
  className?: string
}

export const StatusActionsBadge = ({ count = 0, className }: StatusActionsBadgeProps) => {
  if (!count) return null
  return (
    <Badge bg="info" pill className={className}>
      {count} {count === 1 ? 'action' : 'actions'}
    </Badge>
  )
}

const ALL_STATUSES = [TaskStatusEnum.InProgress, TaskStatusEnum.Complete]

/**
 * Maps a TaskStatusEnum value (e.g., 'In Progress') to its GraphQL enum key (e.g., 'InProgress').
 * GraphQL enums use the TypeScript enum keys, not the runtime values.
 */
const statusEnumKeyMap = Object.fromEntries(
  Object.entries(TaskStatusEnum).map(([key, value]) => [value, key])
) as Record<TaskStatusEnum, string>

// ---------------------------------------------------------------------------
// Props — inline (controlled) vs self-persisting (task-specific)
// ---------------------------------------------------------------------------

type InlineEditorProps = {
  statusActions?: StatusActionInput[]
  onChange: (statusActions: StatusActionInput[]) => void
  taskId?: undefined
  currentStatusActions?: undefined
  onSave?: undefined
  disabled?: boolean
}

type PersistingEditorProps = {
  taskId: string
  currentStatusActions?: TaskStatusActionConfig[]
  onSave?: () => void
  statusActions?: undefined
  onChange?: undefined
  disabled?: boolean
}

export type TaskStatusActionsEditorProps = InlineEditorProps | PersistingEditorProps

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type StatusActionMap = Record<TaskStatusEnum, string>

const buildInitialMap = (actions?: StatusActionInput[] | TaskStatusActionConfig[]): StatusActionMap => {
  const actionMap = {} as StatusActionMap
  for (const status of ALL_STATUSES) {
    actionMap[status] = ''
  }
  if (actions) {
    for (const action of actions) {
      // Normalize: GraphQL returns enum keys (e.g., 'InProgress'), resolve to TS values (e.g., 'In Progress')
      const normalizedStatus = (TaskStatusEnum as Record<string, string>)[action.status] ?? action.status
      if (normalizedStatus && ALL_STATUSES.includes(normalizedStatus as TaskStatusEnum)) {
        actionMap[normalizedStatus as TaskStatusEnum] = action.actionHookId ?? ''
      }
    }
  }
  return actionMap
}

const mapToOutputActions = (actionMap: StatusActionMap): StatusActionInput[] => {
  return ALL_STATUSES.filter((status) => actionMap[status]).map((status) => ({
    status,
    actionHookId: actionMap[status],
  }))
}

const buildHookOptions = (hooks: TaskActionHookType[]) => map(hooks, (hook) => ({ value: hook.id, label: hook.name }))

// ---------------------------------------------------------------------------
// StatusRow — compact inline row
// ---------------------------------------------------------------------------

type StatusRowProps = {
  status: TaskStatusEnum
  actionHookId: string
  hooks: TaskActionHookType[]
  hookOptions: { value: string; label: string }[]
  usedHookIds: Set<string>
  onChange: (status: TaskStatusEnum, actionHookId: string) => void
  disabled?: boolean
}

const StatusRow = ({ status, actionHookId, hooks, hookOptions, usedHookIds, onChange, disabled }: StatusRowProps) => {
  const selectedHook = find(hooks, (h) => h.id === actionHookId)

  // Filter out hooks already assigned to other statuses
  const availableOptions = hookOptions.filter((opt) => opt.value === actionHookId || !usedHookIds.has(opt.value))

  return (
    <div className="d-flex align-items-center gap-2 mb-1">
      <small className="text-muted text-nowrap" style={{ width: 80 }}>
        {status}
      </small>
      <div className="flex-grow-1">
        <Select
          options={availableOptions}
          value={actionHookId ? { value: actionHookId, label: selectedHook?.name ?? actionHookId } : null}
          onChange={(v: unknown) => onChange(status, (v as { value: string } | null)?.value ?? '')}
          placeholder="—"
          isDisabled={disabled}
          isClearable
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// TaskStatusActionsEditor
// ---------------------------------------------------------------------------

export const TaskStatusActionsEditor = (props: TaskStatusActionsEditorProps) => {
  const isInline = 'onChange' in props && !!props.onChange
  // Destructured out of `props` (per the exhaustive-deps guidance) so `emitChange` below depends
  // on this one function instead of the whole `props` object, which changes on every prop.
  const onChangeProp = isInline ? props.onChange : undefined
  const { onError } = useError()
  const { hooks, loading: hooksLoading } = useTaskActionHooks()
  const [executeUpdate, { loading: saving }] = useUpdateTaskStatusActions()

  const initialActions = isInline ? props.statusActions : props.currentStatusActions
  const [actionMap, setActionMap] = useState<StatusActionMap>(() => buildInitialMap(initialActions))
  const savedMapRef = useRef<StatusActionMap>(buildInitialMap(initialActions))
  const hookOptions = useMemo(() => buildHookOptions(hooks), [hooks])

  const isDirty = useMemo(() => !isEqual(actionMap, savedMapRef.current), [actionMap])
  const usedHookIds = useMemo(() => new Set(Object.values(actionMap).filter(Boolean)), [actionMap])

  const emitChange = useCallback(
    (updatedMap: StatusActionMap) => {
      if (isInline && onChangeProp) {
        onChangeProp(mapToOutputActions(updatedMap))
      }
    },
    [isInline, onChangeProp]
  )

  const handleHookChange = useCallback(
    (status: TaskStatusEnum, actionHookId: string) => {
      setActionMap((prev) => {
        const next = { ...prev, [status]: actionHookId }
        emitChange(next)
        return next
      })
    },
    [emitChange]
  )

  const handleSave = async () => {
    if (isInline) return

    try {
      const statusActions = mapToOutputActions(actionMap).map((action) => ({
        status: statusEnumKeyMap[action.status] as TaskStatusEnum,
        actionHookId: action.actionHookId,
      }))

      await executeUpdate({
        filter: { _id: props.taskId },
        statusActions,
      })

      savedMapRef.current = { ...actionMap }
      // Force re-render so isDirty recalculates
      setActionMap((prev) => ({ ...prev }))
      props.onSave?.()
    } catch (error) {
      onError(error)
    }
  }

  const isDisabled = props.disabled || hooksLoading || saving

  if (hooksLoading) {
    return (
      <div className="text-center py-2">
        <Spinner size="sm" />
      </div>
    )
  }

  if (isEmpty(hooks)) {
    return <small className="text-muted">No action hooks available</small>
  }

  const rows = (
    <div>
      {map(ALL_STATUSES, (status) => (
        <StatusRow
          key={status}
          status={status}
          actionHookId={actionMap[status]}
          hooks={hooks}
          hookOptions={hookOptions}
          usedHookIds={usedHookIds}
          onChange={handleHookChange}
          disabled={isDisabled}
        />
      ))}
    </div>
  )

  // -- Inline mode --
  if (isInline) {
    return <div className="py-1">{rows}</div>
  }

  // -- Persisting mode --
  return (
    <div>
      {rows}
      {isDirty && (
        <Alert variant="warning" className="py-1 px-2 mb-0 mt-2 d-flex align-items-center gap-2">
          <Icon name="exclamation-triangle" />
          <small>You have unsaved changes</small>
          <BusyButton
            bsSize="sm"
            variant="warning"
            onClick={handleSave}
            isBusy={saving}
            busyText="Saving..."
            text="Save"
            className="ms-auto"
          />
        </Alert>
      )}
    </div>
  )
}
